import { connectLegacyFinder, createLegacyNearIdentity, createLegacyNearProvenance } from "#ce/apps/SpFinder/main";
import { FINDER_MAPS_API_VERSION } from "#ce/components/SpGoogleMaps/lib/maps-api-version";
import "#legacy/shared/i18n.js";
import "#legacy/shared/accessibility.js";
import "#legacy/utilities/appointment_date.js";
import "./helpers/utils.js";
import "./helpers/recent.js";

import "#/legacy/namespaces.js";

/*
NOTE: Any new options should be added to the DoctorFinderEmbed model, and its corresponding form.
Doctor Finder Options:
  openScheduling: boolean # set to false to disable openScheduling
  rootPath: # defaults to window.location.pathname
  routes: {
    landingPage: string # path where the landing page is found, relative to the rootPath. Do not include a leading /. Defaults to 'find'
    results: string # path where the results page is found, relative to the rootPath. Do not include a leading /. Defaults to ''
  },
  search: # hash of filter params to send to the server.
  ui: {
    showFilters: boolean # set to false to hide filters,
    filters: array # set to limit filters eg. ['skill', 'name', 'gender'],
    specialtyFilterType: string # show either 'Specialty' or 'PracticingSpecialty' as the specialty filter options
    specialtyFilterHierarchy: boolean # if specialtyFilterType=='Specialty' then toggle between a flat/hierarchical list of specialties
    landingPage: {
      show: boolean # set to false to skip the landing page and always show the results
    },
    results: {
      showTitle: boolean # set to false to hide the title
      title: string, or function # set a string to replace the default, or set to a function for full control. Function receives the result count, and the filter descriptions as arguments and should return the desired title as a string.
      count: boolean # set to false to hide the count
      summary: boolean # set to false to hide the summary,
      searchHeader: boolean or array # set to false to hide header, or set an array to limit the search controls eg. ['sort'],
      stickyHeaderOffset: value or callback # defaults to 0 - Offset to add to sticky elements in case other fixed-position elements exist
      mapResultsOcclusion: {
        region: string # A CSS-like bounds string that shrinks a bounding box covering the viewport, such that it only covers the content that would occlude the search results. e.g. '-95% 0px 0px 0px' is a box whose height shrunk 95% so it forms just a thin strip along the bottom of the viewport where the occluding content lies. Once the results enter this region, the occluding function will be executed.
        occluding: function # A function that manipulates the occluding content such that it no longer occludes the results
        out: function # A function that manipulates the occluding content, placing it back in its original position
      },
      notices: boolean # set to false to hide notices,
      doctorMessages: boolean # set to false to hide messages specific to each doctor
      scopeResultsToMapBounds: boolean # set to true if panning the map should scope the results to only those within the map bounds
    }
 }
*/

// Sub-files (models/*.js, views/*.js, collections/*.js) attach themselves to
// window.DoctorFinder.Models / Views / Collections. Under Sprockets these files
// loaded after doctor_finder.js; under Vite the eager glob imports in
// physician_finder/index.js are hoisted and run BEFORE this file. Preserve
// any already-attached entries so we don't blow them away when the class
// definition replaces the previous DoctorFinder namespace object.
const __previousDoctorFinder = window.DoctorFinder;

// Returns one `Backbone.history` route callback that runs `callbacks` in order. It lists them in
// `routeCallbacks`, so a finder can later take its own back out.
function combineRouteCallbacks(callbacks) {
  const combinedCallback = (...args) => callbacks.forEach((callback) => callback(...args));

  combinedCallback.routeCallbacks = callbacks;

  return combinedCallback;
}

window.DoctorFinder = class DoctorFinder {
  static Models = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Models);
  static Views = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Views);
  static Collections = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Collections);
  static Helpers = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Helpers);
  static Templates = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Templates);
  static Serializers = Object.assign({}, __previousDoctorFinder && __previousDoctorFinder.Serializers);
  static Events = __previousDoctorFinder.Events;
  static INSURANCE_PLAN_SESSION_STORAGE_KEY = "insurancePlanFilter";
  static INSURANCE_PLAN_FILTER_NAME = "insurance_plan";
  static LOCATION_SESSION_STORAGE_KEY = "locationFilter";
  static NEAR_FILTER_NAME = "near";

  static {
    // Provide default I18n values for use by unmanaged Doctor Finders
    this.prototype.I18n = new Sparkle.I18n(window.SparkleConfig.doctorFinder.i18n);
  }

  constructor(container, options) {
    const nearIdentity = createLegacyNearIdentity(options, jQuery(container)[0]?.id);
    this.handleRouteToLandingPage = this.handleRouteToLandingPage.bind(this);
    this.handleRouteToResultsPage = this.handleRouteToResultsPage.bind(this);
    this.handleSetScrollPosition = this.handleSetScrollPosition.bind(this);
    this.handleResultsUpdated = this.handleResultsUpdated.bind(this);
    this.handleFirstResultsLoaded = this.handleFirstResultsLoaded.bind(this);
    this.applyLoadingPlaceholderIfNeeded = this.applyLoadingPlaceholderIfNeeded.bind(this);
    this.updateLoadingPlaceholderHeight = this.updateLoadingPlaceholderHeight.bind(this);
    this.teardownLoadingPlaceholder = this.teardownLoadingPlaceholder.bind(this);
    this.handleResultSelected = this.handleResultSelected.bind(this);
    this.handleViewSelected = this.handleViewSelected.bind(this);
    this.handleLandingPageSubmit = this.handleLandingPageSubmit.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.applyDefaultDistanceIfUnset = this.applyDefaultDistanceIfUnset.bind(this);
    this.startBackboneHistory = this.startBackboneHistory.bind(this);
    this.createRouter = this.createRouter.bind(this);
    this.combineDuplicateRoutes = this.combineDuplicateRoutes.bind(this);
    this.normalizeRouterPath = this.normalizeRouterPath.bind(this);
    this.absoluteResultsUrl = this.absoluteResultsUrl.bind(this);
    this.getResultFragment = this.getResultFragment.bind(this);
    this.updateUrlFromFilters = this.updateUrlFromFilters.bind(this);
    this.buildParamsObject = this.buildParamsObject.bind(this);
    this.filterParamsObject = this.filterParamsObject.bind(this);
    this.searchParamsObject = this.searchParamsObject.bind(this);
    this.showStep1 = this.showStep1.bind(this);
    this.showStep2 = this.showStep2.bind(this);
    this.setResultsUrl = this.setResultsUrl.bind(this);
    this.relativeResultsUrl = this.relativeResultsUrl.bind(this);
    this.selectedInsurancePlan = this.selectedInsurancePlan.bind(this);
    this.selectedLocation = this.selectedLocation.bind(this);
    this.locationParamsFromUrl = this.locationParamsFromUrl.bind(this);
    this.applyUIPresetConfiguration = this.applyUIPresetConfiguration.bind(this);
    this.staticPresetConfiguration = this.staticPresetConfiguration.bind(this);
    this.defaultConfiguration = this.defaultConfiguration.bind(this);
    this.normalizeUIOptions = this.normalizeUIOptions.bind(this);
    this.distributeUIConfiguration = this.distributeUIConfiguration.bind(this);
    this.configureOmnisearchStore = this.configureOmnisearchStore.bind(this);
    this.remove = this.remove.bind(this);
    let val = options.rootPath;
    let rootPath = val != null ? val : window.location.pathname;
    let val1 = options.uuid;
    let uuid = val1 != null ? val1 : undefined;
    let val2 = options.openScheduling;
    let openScheduling = val2 != null ? val2 : true;
    let val3 = options.passiveBoosts;
    let passiveBoosts = val3 != null ? val3 : true;
    let val4 = options.reloadOnLandingPageSubmit;
    let reloadOnLandingPageSubmit = val4 != null ? val4 : false;
    let val5 = options.requireLocation;
    let requireLocation = val5 != null ? val5 : false;
    let val6 = options.askDeviceForLocation;
    let askDeviceForLocation = val6 != null ? val6 : false;
    let val7 = options.geocodeIP;
    let geocodeIP = val7 != null ? val7 : false;
    let val8 = options.routes;
    let routes = val8 != null ? val8 : {};
    let val9 = options.search;
    let search = val9 != null ? val9 : {};
    let val10 = options.defaultSearch;
    let defaultSearch = val10 != null ? val10 : {};
    let val11 = options.ui;
    let ui = val11 != null ? val11 : {};
    ui.i18n = jQuery.extend(true, {}, this.I18n.backend, ui.i18n); // Merge the given I18n values with the defaults
    // Expose the fully-merged (localized) catalog finder-wide so any view whose
    // I18n wasn't threaded through the constructor chain can still resolve i18n
    // (see Base#i18nForRender) instead of rendering with an undefined I18n.
    DoctorFinder.Views.Base.fallbackI18n = new Sparkle.I18n(ui.i18n);

    this.locationSettings = {
      requireLocation,
      askDeviceForLocation,
      geocodeIP,
      filterOptions: ui.filterOptions,
      commonPlaces: ui.commonLocations,
    };

    this.uuid = uuid;
    ui = this.applyUIPresetConfiguration(ui, ui.preset); // Assign presets
    this.normalizeUIOptions(ui); // Normalize and distribute configuration to landing page and results page configs

    if (ui.results.maxResults > ui.results.defaultPerPage) {
      console.warn(
        "maxResults can not be greater per page. Setting max results from: ",
        ui.results.maxResults,
        "to: ",
        ui.results.defaultPerPage,
      );
      ui.results["maxResults"] = ui.results.defaultPerPage;
    }

    ui.results = _.defaults(ui.results, {
      hideMapView,
      pagination: ui.results.maxResults ? false : undefined, // If there is a limit on the results, do not allow for pagination. Otherwise results may be missing
    });

    // Track whether the consumer explicitly opted into showing the next appointment so we can
    // override the static suggester preset's doctorMessages = false below. We can't infer this
    // from the value after `_.defaults` applies because the default is also true.
    const explicitlyShowProviderNextAppointment = ui.results.result?.showProviderNextAppointment === true;

    ui.results.result = _.defaults({}, ui.results.result, {
      showProviderRatings: true,
      showProviderSpecialty: true,
      showProviderAddress: true,
      showProviderVisitType: true,
      showProviderAcceptingPatients: true,
      showProviderNotAcceptingPatients: false,
      showProviderAgeGroup: true,
      showProviderNextAppointment: true,
      showProviderHoursBadge: false,
      hideAppointmentGrid: ui.results.searchHeader === false,
      showMapLink: true,
      showMultipleSchedulingTypes: true,
    });

    // The static suggester presets disable doctorMessages, which suppresses the scheduling
    // messaging that is the visible "next appointment" content for providers without bookable
    // slots. Re-enable it when the Next Appointment toggle is explicitly on so the toggle has a
    // visible effect on those presets without changing behaviour for embeds that don't opt in.
    if (explicitlyShowProviderNextAppointment && !ui.results.doctorMessages) {
      ui.results.doctorMessages = true;
    }

    this.$el = jQuery(container);
    this.el = this.$el[0];

    if (ui.results.result.showMapLink === true && hideMapView === true) {
      console.warn("showMapLink cannot be true if the map view is hidden. Setting showMapLink to false");
      ui.results.result["showMapLink"] = false;
    }

    var hideMapView =
      (_.isArray(ui.results.searchHeader) && ui.results.searchHeader.indexOf("view") < 0) ||
      ui.results.searchHeader === false;
    this.autoApplyInsurancePlanFilter =
      ui.showFilters && DoctorFinder.Helpers.isFilterEnabled(ui.filters, this.constructor.INSURANCE_PLAN_FILTER_NAME);
    // Only auto-apply a persisted location when the Near filter is actually exposed, so we don't silently scope finders that hide it
    this.autoApplyLocationFilter =
      !!this.locationSettings.filterOptions?.persist_location &&
      ui.showFilters &&
      DoctorFinder.Helpers.isFilterEnabled(ui.filters, this.constructor.NEAR_FILTER_NAME);

    this.routes = _.defaults(routes, { landingPage: "find", results: "" });
    this.nearProvenance = createLegacyNearProvenance(nearIdentity);
    this.hasLandingPage = ui.landingPage && ui.landingPage.show;

    this.reloadOnLandingPageSubmit = reloadOnLandingPageSubmit;
    this.router = this.createRouter(this.routes);

    // There's no sense in scoping to an empty filter value. Ignore blank values because those may have been set programmatically from some value that ended up not being present.
    search = Sparkle.Utilities.removeBlankParams(search);
    this.defaultSearch = Sparkle.Utilities.removeBlankParams(defaultSearch);
    this.results = new DoctorFinder.Collections.Results({
      scope: search,
      openScheduling,
      passiveBoosts,
      slotsUI: ui.slotsUI,
      defaultPerPage: ui.results.defaultPerPage,
    });
    this.results.pauseFetching(); // Disable any fetches until we've initialized the filters from the URL

    // The omnisearch/autocomplete data now lives in a shared Vue store (see the
    // custom_elements entrypoint). Hand it this embed's fetch config + omnisearch
    // options once; <sp-finder-omnisearch> and the specialty/provider facets read it.
    this.configureOmnisearchStore(ui, search);

    this.finderAdapter = connectLegacyFinder(this.el);
    if (this.hasLandingPage) {
      this.landingPage = new DoctorFinder.Views.WizardStep1(
        _.extend({}, { uuid: this.uuid }, ui.landingPage, {
          finderAdapter: this.finderAdapter,
          locationSettings: this.locationSettings,
          el: this.$el.find("#wizard-step-1").get(0),
          collection: this.results,
          i18n: ui.i18n,
        }),
        {
          stickyHeaderOffset: ui.results.stickyHeaderOffset,
        },
      );
    }

    this.resultsPage = new DoctorFinder.Views.WizardStep2(
      _.extend({}, { uuid: this.uuid }, ui.results, {
        openScheduling,
        visitTypeFilterOwnsParam: ui.visitTypeFilterOwnsParam,
        locationSettings: this.locationSettings,
        resultTypes: options.search.result_types,
        el: this.$el.find("#wizard-step-2").get(0),
        onNearProvenanceChange: (params, metadata) => this.nearProvenance?.queue(params, metadata),
        onNearProvenanceUnchanged: () => this.nearProvenance?.prepare(this.results.filters)(),
        collection: this.results,
        i18n: ui.i18n,
      }),
    );
    this.initialLoad = true; // We have yet to show the landing or results pages

    if (this.$el.is("[data-df-loading]")) {
      this.loadingPlaceholderActive = true;
      if (!this.placeholderResizeHandler) {
        this.placeholderResizeHandler = () => this.updateLoadingPlaceholderHeight();
      }
      jQuery(window).on("resize", this.placeholderResizeHandler);
      this.updateLoadingPlaceholderHeight();
    }

    // BEHAVIOUR

    window.Sparkle.Accessibility.clickableWithKeyboard(this.$el);
    if (this.hasLandingPage) {
      this.router.on("route:landingPage", this.handleRouteToLandingPage);
    }
    this.router.on("route:results", this.handleRouteToResultsPage);
    this.results.on("change:filters", this.handleFilterChange);
    this.results.on("change:results", this.handleResultsUpdated);
    this.results.once("change:results", this.handleSetScrollPosition);
    this.results.once("sync", this.handleFirstResultsLoaded);
    this.results.on("change:selectedResult", this.handleResultSelected);
    this.results.on("change:selectedView", this.handleViewSelected);

    // INITIALIZE

    this.finderAdapter.reportSettings({
      hasLandingPage: Boolean(this.hasLandingPage),
      landing: {
        requireSkill: Boolean(ui.landingPage?.requireSkill),
        sections: ui.landingPage?.sections,
        filters: ui.landingPage?.filters,
        showFilters: ui.landingPage?.showFilters,
        skillSetsNewPatient: Boolean(window.SparkleConfig.doctorFinder.doctorSkillSearchSetNewPatientFlag),
        featuredNavigation: ui.landingPage?.featured_navigation,
        textSearchEnabled: Boolean(window.SparkleConfig.doctorFinder.omnisearchTextSearchEnabled),
      },
      location: {
        ...this.locationSettings,
        url: (window.Sparkle.apiHost || "") + window.SparkleConfig.routes.embed.filterSettings,
        geocodeUrl: (window.Sparkle.apiHost || "") + window.SparkleConfig.routes.embed.geocodeAddresses,
      },
      results: this.resultsSettings(ui.results, ui.i18n),
      map: this.mapSettings(ui.results),
    });
    this.finderAdapter.setActions(this.finderActions());
    this.startBackboneHistory(rootPath);
  }

  // The results page options the `sp-finder-result-view` block renders from.
  resultsSettings(results, i18n) {
    return {
      noAddressLabel: i18n?.physicians?.physician?.no_address,
      result: { ...results.result, resultTemplate: results.resultTemplate },
      listLayout: results.listLayout,
      maxResults: results.maxResults,
      pagination: results.pagination,
      notices: results.notices,
      doctorMessages: results.doctorMessages,
      hideMapView: results.hideMapView,
      goToDate: Boolean(window.SparkleConfig.doctorFinder.featureFlags?.goToDate),
      availabilityUrl: (window.Sparkle.apiHost || "") + window.SparkleConfig.routes.embed.availabilityPhysicians,
      mapBannerImageUrl: window.SparkleConfig.doctorFinder.mapBannerImageUrls?.[window.Sparkle.currentPublishingDomain],
      currentWebsite: Sparkle.Embed?.Utilities?.detectCurrentWebsite?.(),
    };
  }

  // Returns the `sp-finder-map` settings resolved from the boot configuration.
  mapSettings(results) {
    const config = window.SparkleConfig;
    const domain = Sparkle.currentPublishingDomain;
    const baseURL = window.Sparkle.apiHost || "";
    const resolveAsset = (path) => path && (/^https?:\/\//.test(path) ? path : baseURL + path);
    const selected = resolveAsset(
      config.doctorFinder.themedSelectedPinUrls?.[domain] || config.assetPaths?.mapPinHighlight,
    );
    const center = Sparkle.Config?.googleMaps?.mapCenter;
    return {
      icons: {
        default: resolveAsset(config.doctorFinder.themedPinUrls?.[domain] || config.assetPaths?.mapPinDefault),
        selected,
        clusterSelected: resolveAsset(config.assetPaths?.multipinHighlight) || selected,
        micro: resolveAsset(config.doctorFinder.themedMicropinUrls?.[domain] || config.assetPaths?.micropinDefault),
      },
      clusterMarkers: Boolean(config.doctorFinder.clusterMarkers),
      defaultCenter: center ? { lat: center.lat, lng: center.lng } : undefined,
      defaultZoom: center?.zoom,
      boundsScopingEnabled: Boolean(config.doctorFinder.featureFlags?.scopeResultsToMapBounds),
      scopeResultsToMapBounds: Boolean(results.scopeResultsToMapBounds),
      autoBounds: Boolean(this.resultsPage.shouldScopeToMapBoundsByDefault()),
      stickyOffset: this.resultsPage.mapStickyOffset(),
      coordinatesUrl: config.routes.embed.coordinatesPhysicians,
      pinInfoUrl: config.routes.embed.mapPinInfoPhysicians,
      baseURL,
      apiKey: Sparkle.apiKeys?.googleMaps ?? "",
      apiVersion: FINDER_MAPS_API_VERSION,
    };
  }

  // TEARDOWN

  // Releases this finder's views, its route registrations and router/results listeners, and the
  // shared omnisearch store. Disposing stops the store's in-flight fetch/poll loop and frees the
  // per-uuid instance; `releaseOmnisearch: false` keeps a store another live finder still reads.
  remove({ releaseOmnisearch = true } = {}) {
    this._torndown = true;
    if (this.applyOmnisearchConfig) {
      window.removeEventListener("sp:elements:initialized", this.applyOmnisearchConfig);
    }
    this.teardownLoadingPlaceholder();
    this.landingPage?.remove();
    this.resultsPage?.remove();
    this.finderAdapter?.disconnect();
    this.releaseRoutes();
    this.router?.off();
    this.results?.off();

    if (releaseOmnisearch) {
      window.Sparkle?.DoctorFinderOmnisearch?.dispose?.(this.uuid);
    }
  }

  // Takes this finder's router callbacks out of `Backbone.history`, including callbacks combined
  // with another finder's, which keep running.
  releaseRoutes() {
    const owned = new Set(this.routeCallbacks);

    if (!owned.size || !Backbone.history.handlers) {
      return;
    }

    Backbone.history.handlers = Backbone.history.handlers.flatMap((handler) => {
      const callbacks = (handler.callback.routeCallbacks ?? [handler.callback]).filter(
        (callback) => !owned.has(callback),
      );

      if (callbacks.length === 0) {
        return [];
      }

      return [{ route: handler.route, callback: combineRouteCallbacks(callbacks) }];
    });
    this.routeCallbacks = [];
  }

  // EVENT HANDLERS

  handleRouteToLandingPage() {
    let insurancePlan;
    if (this.autoApplyInsurancePlanFilter && (insurancePlan = this.selectedInsurancePlan())) {
      this.landingPage.setInsurancePlan(insurancePlan);
    }

    const storedLocation = this.autoApplyLocationFilter && this.selectedLocation();
    if (storedLocation) {
      this.landingPage.setLocation(storedLocation);
    }

    return this.showStep1();
  }

  handleRouteToResultsPage(paramsString) {
    if (this.finderAdapter.route !== "results") {
      this.results.reset();
    } // Don't show previous results if we transition to step 2, but do show them in order to prevent flash of no content if we're already here
    let paramsObject = this.buildParamsObject(paramsString);
    const nearMetadata = this.nearProvenance?.restore(paramsObject);

    // Apply the insurance plan filter by default on first load if we have one saved in session storage
    let insurancePlan;
    if (this.autoApplyInsurancePlanFilter && (insurancePlan = this.selectedInsurancePlan())) {
      paramsObject.insurance_plan = insurancePlan;
    } else if (
      this.autoApplyInsurancePlanFilter &&
      paramsObject.insurance_plan &&
      this.locationSettings.filterOptions?.persist_insurance_plan
    ) {
      // Persist an insurance plan that arrived on the URL so later loads can restore it from session storage
      window.sessionStorage.setItem(this.constructor.INSURANCE_PLAN_SESSION_STORAGE_KEY, paramsObject.insurance_plan);
    }

    // Seed the location from session storage when one is saved, otherwise from any location params on the URL.
    let location;
    if (!nearMetadata && this.autoApplyLocationFilter && (location = this.selectedLocation())) {
      delete paramsObject.zip;
      delete paramsObject.near;
      delete paramsObject.lat;
      delete paramsObject.lng;
      delete paramsObject.distance;
      if (location.zip != null) {
        paramsObject.zip = location.zip;
      }
      if (location.lat != null) {
        paramsObject.lat = location.lat;
      }
      if (location.lng != null) {
        paramsObject.lng = location.lng;
      }
      if (location.distance != null) {
        paramsObject.distance = location.distance;
      }
    } else {
      const urlLocation = this.locationParamsFromUrl(paramsObject);
      if (urlLocation) {
        if (urlLocation.zip != null) {
          paramsObject.zip = urlLocation.zip;
        }
        delete paramsObject.near;
        if (!nearMetadata && this.autoApplyLocationFilter) {
          this.resultsPage.persistLocation(urlLocation);
        }
      }
    }

    if (this.initialLoad) {
      paramsObject = this.applyDefaultDistanceIfUnset(paramsObject);
    }

    const searchParams = this.searchParamsObject(paramsObject);

    // Apply the default search if the search is blank. NOTE: This only checks if filters have been applied because there
    // isn't an authoritative list of params we would check other than filters. It does, however, merge existing params
    // on top of the default search. So if the default search contains a param that is present in the current url, it
    // will retain it.
    if (
      this.initialLoad &&
      !this.defaultSearchApplied &&
      !jQuery.isEmptyObject(this.defaultSearch) &&
      jQuery.isEmptyObject(Sparkle.Utilities.removeBlankParams(this.filterParamsObject(searchParams)))
    ) {
      this.defaultSearchApplied = true; // Flag to make it impossible to perform this step more than once per initialization. Just in case we somehow don't update the params how we expected and would get into an infinite loop.

      // Navigate to the default search url if we aren't already there.
      if (window.location.href !== this.absoluteResultsUrl(this.defaultSearch, paramsObject)) {
        Backbone.history.navigate(this.relativeResultsUrl(this.defaultSearch, paramsObject), {
          trigger: true,
          replace: true,
        });
        return;
      }
    }

    if (nearMetadata) {
      this.resultsPage.setInputsFromJSON(paramsObject, nearMetadata);
    } else {
      this.resultsPage.setInputsFromJSON(paramsObject);
    }

    this.results.selectResult(paramsObject["result_guid"]);
    const viewSelected = this.results.selectView(paramsObject["view"]);

    // Set filter, this will trigger API request.
    // Applying filters from the URL on route load is normalization, not a user action. Replace the
    // history entry instead of pushing a new one (the intent survives the paused-fetch deferral).
    const filtersSet = this.results.setFilters(searchParams, { replaceBrowserHistory: true });

    // We only need to render the results if view or filters change since selecting a doctor is handled without the need to render
    jQuery.when(viewSelected, filtersSet).then((...changed) => {
      if (_.contains(changed, true)) {
        return this.resultsPage.render();
      }
    });

    this.resultsPage.updateFiltersVisibility(); // Update the filters based on whether the user applied any filters or not
    return this.showStep2();
  }

  handleSetScrollPosition() {
    let fragment;
    if (!(fragment = this.getResultFragment())) {
      return;
    }

    return setTimeout(() => {
      const result = jQuery(`[data-result-guid=${fragment}]`)[0];
      if (result) {
        return this.resultsPage.scrollToTop({
          element: result,
          offset: -this.resultsPage.searchResultsView.getStickyHeaderOffset(),
        });
      }
    }, 500);
  }

  handleResultsUpdated(results, resultsRequestParams, resultsRequestUrl) {
    this.resultsRequestParams = resultsRequestParams;
    this.resultsRequestUrl = resultsRequestUrl;
    return this.trackSearch();
  }

  handleFirstResultsLoaded() {
    return this.teardownLoadingPlaceholder();
  }

  applyLoadingPlaceholderIfNeeded() {
    if (this.results != null ? this.results.isFetched : undefined) {
      return;
    }

    if (this.loadingPlaceholderActive) {
      this.updateLoadingPlaceholderHeight();
      return;
    }

    this.loadingPlaceholderActive = true;
    this.$el.attr("data-df-loading", "true");

    if (!this.placeholderResizeHandler) {
      this.placeholderResizeHandler = () => this.updateLoadingPlaceholderHeight();
    }
    jQuery(window).on("resize", this.placeholderResizeHandler);

    return this.updateLoadingPlaceholderHeight();
  }

  updateLoadingPlaceholderHeight() {
    if (!this.loadingPlaceholderActive) {
      return;
    }

    let viewportHeight = typeof window !== "undefined" && window !== null ? window.innerHeight : undefined;
    if (viewportHeight == null) {
      viewportHeight = document?.documentElement?.clientHeight || 0;
    }
    const stickyOffset =
      (typeof this.resultsPage?.searchResultsView?.getStickyHeaderOffset === "function"
        ? this.resultsPage.searchResultsView.getStickyHeaderOffset()
        : undefined) || 0;
    const minHeight = Math.max(viewportHeight - stickyOffset, 0);

    if (minHeight > 0) {
      return this.$el.css("min-height", `${minHeight}px`);
    } else {
      return this.$el.css("min-height", "");
    }
  }

  teardownLoadingPlaceholder() {
    if (!this.loadingPlaceholderActive) {
      return;
    }

    this.loadingPlaceholderActive = false;
    this.$el.removeAttr("data-df-loading");
    this.$el.css("min-height", "");
    this.$el.css("display", "");
    this.$el.removeAttr("style");

    if (this.placeholderResizeHandler) {
      jQuery(window).off("resize", this.placeholderResizeHandler);
      return (this.placeholderResizeHandler = null);
    }
  }

  handleResultSelected(selectedResultGUID) {
    const paramsObject = this.buildParamsObject();
    return this.setResultsUrl({ replaceBrowserHistory: true }, paramsObject, { result_guid: selectedResultGUID });
  }

  handleViewSelected(view) {
    const paramsObject = this.buildParamsObject();
    this.resultsPage.updateInputsFromJSON({ view });
    this.setResultsUrl({ replaceBrowserHistory: true }, paramsObject, { view });
    this.resultsPage.render();
    return this.trackSearch();
  }

  handleLandingPageSubmit(filters, metadata) {
    filters = {
      ...this.defaultSearch,
      ...filters,
    }; // Apply default search filters
    filters = this.applyDefaultDistanceIfUnset(filters);

    // A 10-digit query is a National Provider Identifier, not searchable free text. Leave the SPA
    // and full-load the results URL so a server-side NPI->profile redirect can intercept it and
    // deep-link the visitor straight to the provider. Finders whose backend has no such redirect
    // just render the results page for the query, exactly as a normal full-page submit would.
    if (this.reloadOnLandingPageSubmit || this.isNpiQuery(filters.query)) {
      const destination = this.absoluteResultsUrl(filters);
      this.nearProvenance?.stage(destination, filters, metadata);
      return (window.location = destination);
    } else {
      this.nearProvenance?.queue(filters, metadata);
      this.resultsPage.updateFiltersVisibility(); // Update the filters based on whether the user applied any filters or not
      const filtersForInputs = this.searchParamsObject(filters);
      this.resultsPage.updateInputsFromJSON(filtersForInputs, metadata); // Keep filter UI in sync immediately to avoid flashing stale chips
      this.results.reset(); // Don't show previous results while performing new search submitted from landing page. It feels confusing because you see results, but then it grays out after a moment depending on how long the search takes.

      this.results.setFilters(filters).then((changed) => {
        if (!jQuery.isEmptyObject(this.results.filterDescriptions)) {
          DoctorFinder.Helpers.recordRecentSearch(this.uuid, this.results.searchSentence, filters);
        }
        // If we didn't change the filters, the filter change handler will not have updated the url so we do it manually
        if (!changed) {
          return this.updateUrlFromFilters(filters);
        }
      });
      this.showStep2();
      // onlyScrollUp: never scroll down — submitting from the top of a host page would clip the host site's header
      return this.resultsPage.scrollToTop({
        offset: -this.resultsPage.searchResultsView.getStickyHeaderOffset(),
        onlyScrollUp: true,
      });
    }
  }

  handleFilterChange(filters, resultsRequestParams, resultsRequestUrl, options = {}) {
    filters = this.searchParamsObject(filters);
    this.results.selectResult(null);
    this.resultsPage.updateInputsFromJSON(filters);
    // Route-load normalization marks itself replace (no new history entry); user-initiated changes
    // carry no option and keep pushing, so the back button still works for real actions.
    return this.updateUrlFromFilters(filters, { replaceBrowserHistory: !!options.replaceBrowserHistory });
  }

  // HELPER FUNCTIONS

  // If the default distance is set and the user has not selected a distance, we need to set it to the default distance.
  // If the user has deliberately selected 'Any Distance', we don't want to override it.
  // This is to ensure that the default distance is always set when the user has not selected a distance.
  applyDefaultDistanceIfUnset(paramsObject) {
    if (!paramsObject.distance && paramsObject.distance !== DoctorFinder.Views.LocationSearch.ANY_DISTANCE) {
      return Object.assign({}, paramsObject, {
        distance: this.locationSettings.filterOptions?.distance?.default,
      });
    }

    return paramsObject;
  }

  // A National Provider Identifier is exactly 10 digits; treat such a query as an NPI lookup
  // rather than free-text search (see handleLandingPageSubmit).
  isNpiQuery(query) {
    return /^\d{10}$/.test(String(query ?? "").trim());
  }

  startBackboneHistory(rootPath) {
    this.withoutModifyingHash(() => {
      // Avoid treating the hash as part of the path. Backbone has legacy support where the hash is used to store dynamic path changes, but we're not using that and don't want direct links to in-page anchors to be treated as such.
      this.combineDuplicateRoutes(); // If we start multiple doctor finders on the same page, we will create duplicate routes, with separate callbacks. Backbone only triggers the first matching one, so we need to combine them all into a single handler.

      if (!Backbone.History.started) {
        return Backbone.history.start({
          silent: true, // We trigger the initial url update manually because we may have multiple finders on the same page and each needs to initialize
          pushState: true,
          root: rootPath,
        });
      }
    }); // NOTE: If we're running multiple doctor finders on the same page, they should all use the same rootPath because only the first one will be used

    return Backbone.history.loadUrl(); // Initialize the finder based on the current url
  }

  // Records the callbacks the router adds to `Backbone.history` in `routeCallbacks`, for
  // `releaseRoutes`.
  createRouter(routes = {}) {
    const registered = new Set((Backbone.history.handlers ?? []).map((handler) => handler.callback));
    const router = new Backbone.Router({
      routes: {
        [this.normalizeRouterPath(routes.results)]: "results",
        [this.normalizeRouterPath(routes.landingPage)]: this.hasLandingPage ? "landingPage" : undefined,
      },
    });

    this.routeCallbacks = (Backbone.history.handlers ?? [])
      .map((handler) => handler.callback)
      .filter((callback) => !registered.has(callback));

    return router;
  }

  // Combine Backbone.History handlers with the same route into a single callback so we trigger all matching callbacks instead of just the first
  combineDuplicateRoutes() {
    if (!Backbone.history.handlers) {
      return;
    }

    const map = new Map();
    Backbone.history.handlers.forEach((handler) => {
      // Convert Map#keys to an array because Safari doesn't support Iterator.find()
      let matchingRoute = Array.from(map.keys()).find((route) => route.toString() === handler.route.toString());
      if (!matchingRoute) {
        matchingRoute = handler.route;
        map.set(handler.route, []);
      }
      return map.get(matchingRoute).push(...(handler.callback.routeCallbacks ?? [handler.callback]));
    });

    // Combine handlers into a single callback
    const handlers = [];
    map.forEach((callbacks, route) => handlers.push({ route, callback: combineRouteCallbacks(callbacks) }));

    return (Backbone.history.handlers = handlers);
  }

  // Ensures there is an optional trailing slash to maximize compatibility
  normalizeRouterPath(path) {
    return (path || "").replace(/\/$/, "") + "(/)";
  }

  absoluteResultsUrl(...paramsObjects) {
    const relativeUrl = Backbone.history.root + this.relativeResultsUrl(...Array.from(paramsObjects || []));
    try {
      const url = new URL(relativeUrl, window.location.origin);
      if (url.pathname.endsWith("/")) {
        url.pathname = url.pathname.slice(0, -1);
      }
      return url.toString();
    } catch {
      // e.g. srcdoc preview iframe where origin is the literal string "null"
      return relativeUrl;
    }
  }

  getResultFragment() {
    return window.location.hash.slice(1);
  }

  updateUrlFromFilters(filters, options = {}) {
    // `result_guid: null` drops the current doctor (we're starting a new search). `near: null` strips the
    // inbound `near` alias — it's canonicalized to `zip` on route load and is not a filter input, so
    // without this it would linger in the rebuilt URL beside `zip` (e.g. from a shared `?near=` link).
    const drops = { result_guid: null, near: null };

    // Entry-flow URL metadata (`provider_search_context` and the `result_types` override it gates —
    // a "who works here" search) only rides a URL that still carries the `location` entry anchor.
    // When a new search supersedes the anchor (see wizard_step_2's clearing rules), the metadata
    // drops with it: left behind it would misdescribe a now-normal search and revive the entry flow
    // (results heading context, embed result_types override) on a reload or share of that URL.
    const currentParams = this.buildParamsObject();
    const anchor = filters && "location" in filters ? filters.location : currentParams.location;
    if (currentParams.provider_search_context && !anchor) {
      drops.provider_search_context = null;
      drops.result_types = null;
    }

    return this.setResultsUrl(options, currentParams, filters, drops);
  }

  buildParamsObject(paramString = window.location.search) {
    return Sparkle.Utilities.paramsToObject(paramString);
  }

  // Returns an object with only the params for which the physician search has a search filter with the same name
  filterParamsObject(paramsObject) {
    return _.pick(paramsObject, ...Array.from(window.SparkleConfig.doctorFinder.registeredFilters));
  }

  // NOTE: This returns non-filter params like page, view, etc.
  searchParamsObject(paramsObject) {
    const output = {};
    this.resultsPage.availableFilters.forEach(
      (
        filterName, // Only include keys that are backed by input elements so we don't start working with params that don't belong to us
      ) => (output[filterName] = paramsObject[filterName] || null),
    ); // Clear filter params that are not in the new set

    return output;
  }

  showStep1() {
    this.finderAdapter.reportRoute("landing");
  }

  showStep2() {
    this.finderAdapter.reportRoute("results");
  }

  consumeFinderRoute(route) {
    const showing = route === "landing" ? "step1" : "step2";
    if (this.showing === showing) {
      return;
    }
    if (!this.initialLoad) {
      this.trackPageview();
    }
    this.showing = showing;
    this.landingPage?.visible(route === "landing");
    this.resultsPage.visible(route === "results");
    if (route === "landing") {
      this.teardownLoadingPlaceholder();
    } else {
      this.applyLoadingPlaceholderIfNeeded();
      // Required filters retain ownership of releasing a gated results request.
      if (!(
        this.el?.closest("sp-finder")?.fetchingHeld || this.el?.querySelector("sp-required-filters-modal[auto-open]")
      )) {
        this.results.resumeFetching();
      }
    }
    this.initialLoad = false;
  }

  trackPageview() {
    return this.$el.trigger("pageview.sparkle");
  }

  // The latest search as the `sparkle:search` event describes it, or null before
  // the first search. Lets a component that mounts after that event read it.
  currentSearch() {
    if (!this.resultsRequestParams) {
      return null;
    }

    const params = {
      ...this.resultsRequestParams,
      view: this.results.selectedView,
      visit_type_name: this.resultsPage.selectedVisitTypeText(),
    };

    return {
      source: "doctor-finder",
      params: params,
      url: this.resultsRequestUrl,
      resultCount: this.results.resultCount,
    };
  }

  // Actions the `<sp-finder>` wrapper forwards to this instance.
  finderActions() {
    return {
      routeSurfaces: { landing: this.landingPage?.el, results: this.resultsPage.el },
      ...this.resultsPage.resultActions(),
      ...this.resultsPage.mapActions(),
      consumeRoute: (route) => this.consumeFinderRoute(route),
      landingDraftChanged: () => this.landingPage?.decorateSearchLinks(),
      submitLandingSearch: (draft, metadata = { transientLocation: false }) =>
        this.handleLandingPageSubmit(draft, metadata),
      navigate: ({ url, suggestedFilters, query, route, params }) => {
        if (suggestedFilters) {
          jQuery(document).trigger("autocomplete-select.sparkle", ["chatterbox", query]);
        }
        if (url) {
          return DoctorFinder.Helpers.navigateToUrlUsingAnchorTag(
            this.landingPage.decorateProfileUrl(url),
            this.landingPage.el,
          );
        }
        if (route === "landing" && this.hasLandingPage) {
          return this.router.navigate(this.routes.landingPage, { trigger: true });
        }
        if (route === "results") {
          return this.handleLandingPageSubmit(params ?? this.finderAdapter.landingDraft ?? {});
        }
      },
      persistSettings: () => ({
        persistLocation: Boolean(this.locationSettings.filterOptions?.persist_location),
        persistInsurancePlan: Boolean(this.locationSettings.filterOptions?.persist_insurance_plan),
      }),
      releaseFetching: (options) => this.releaseFetching(options),
      applyRequiredFilters: (selection) => this.applyRequiredFilters(selection),
    };
  }

  // Resumes the paused results fetch. On the landing page there is no search yet, so the pause flag is cleared
  // directly: `resumeFetching` there would navigate to the results route.
  releaseFetching({ insurancePlan } = {}) {
    if (!this.results) {
      return;
    }

    if (this.finderAdapter.route === "results") {
      // The paused search takes the validated plan, so the resumed fetch does not use a stale stored one.
      if (insurancePlan !== undefined) {
        this.results.updateFilters?.({ insurance_plan: insurancePlan });
      }
      this.results.resumeFetching?.();
    } else {
      this.results.fetchingPaused = false;
    }
  }

  // Applies the required-filters selection: active filters on the results page, a pre-filled form on the landing page.
  applyRequiredFilters({ location, insurance }) {
    const geolocatedLabel =
      (this.landingPage ?? this.resultsPage)?.i18nForRender?.()?.t?.("doctor_finder.placeholders.geolocated") ??
      "Current Location";
    const locationDisplayName = location.zip ?? geolocatedLabel;

    function persistLocation(view) {
      if (Object.keys(location).length === 0) {
        return;
      }
      view?.persistLocation?.({
        zip: location.zip,
        distance: location.distance,
        lat: location.lat,
        lng: location.lng,
        locationDisplayName,
      });
    }

    if (this.finderAdapter.route === "results" && this.results) {
      const filters = { ...location };
      if (insurance) {
        filters.insurance_plan = insurance;
      }
      persistLocation(this.resultsPage);
      this.results.updateFilters(filters, { resetPage: true });
      return;
    }

    const landing = this.landingPage;
    if (!landing) {
      return;
    }

    if (Object.keys(location).length > 0) {
      landing.applyLocationFilter?.(location);
      landing.locationSearch?.setState?.({
        location: locationDisplayName,
        locationDisplayName,
        lat: location.lat,
        lng: location.lng,
        distance: location.distance,
      });
      landing.updateRenderedLocation?.();
      persistLocation(landing);
    }

    if (insurance) {
      landing.applyInsurancePlanFilter?.(insurance);
      landing.setInsurancePlan?.(insurance);
    }
  }

  trackSearch() {
    const detail = this.currentSearch();
    if (!detail) {
      return;
    }

    this.$el.trigger("search.sparkle", ["doctor-finder", detail.params, detail.url, detail.resultCount]);
    // Native twin of the jQuery-only event above, for custom elements (e.g.
    // sp-directory-pdf-export) that cannot subscribe via jQuery.
    return this.el.dispatchEvent(new CustomEvent("sparkle:search", { bubbles: true, detail: detail }));
  }

  setResultsUrl(options, ...paramsObjects) {
    const replaceBrowserHistory = options.replaceBrowserHistory || false; // Default to adding a new browser history entry for each resultsUrl
    const resultsUrl = this.relativeResultsUrl(...Array.from(paramsObjects || []));
    const finishNearEntry = this.nearProvenance?.prepare(jQuery.extend({}, ...paramsObjects));
    if (Backbone.history.fragment !== resultsUrl) {
      // don't do anything if the params haven't changed
      const navigated = Backbone.history.navigate(resultsUrl, { trigger: false, replace: replaceBrowserHistory });
      finishNearEntry?.();
      return navigated;
    }
    finishNearEntry?.();
  }

  relativeResultsUrl(...paramsObjects) {
    const path = this.routes.results;
    const params = Sparkle.Utilities.paramsFromObject(jQuery.extend({}, ...Array.from(paramsObjects)));

    return `${path}${params}`;
  }

  selectedInsurancePlan() {
    if (!this.locationSettings.filterOptions?.persist_insurance_plan) {
      return null;
    }
    return window.sessionStorage.getItem(this.constructor.INSURANCE_PLAN_SESSION_STORAGE_KEY);
  }

  selectedLocation() {
    if (!this.locationSettings.filterOptions?.persist_location) {
      return null;
    }
    const stored = window.sessionStorage.getItem(this.constructor.LOCATION_SESSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    let location;
    try {
      location = JSON.parse(stored);
    } catch (e) {
      window.sessionStorage.removeItem(this.constructor.LOCATION_SESSION_STORAGE_KEY); // Drop the corrupt value so we don't keep re-parsing it
      return null;
    }
    // Require a zip or a complete coordinate pair
    if (!(location && (location.zip != null || (location.lat != null && location.lng != null)))) {
      return null;
    }
    return location;
  }

  locationParamsFromUrl(paramsObject) {
    const zip = paramsObject?.zip || paramsObject?.near;
    const lat = paramsObject?.lat;
    const lng = paramsObject?.lng;
    if (!(zip || (lat && lng))) {
      return null;
    }
    const location = {};
    if (zip != null) {
      location.zip = zip;
    }
    if (paramsObject?.distance != null) {
      location.distance = paramsObject.distance;
    }
    if (lat && lng) {
      location.lat = lat;
      location.lng = lng;
    }
    if (zip != null) {
      location.locationDisplayName = zip;
    }
    return location;
  }

  withoutModifyingHash(fn) {
    const { hash } = window.location;
    window.location.hash = "";
    try {
      return fn();
    } finally {
      window.location.hash = hash;
    }
  }

  // Hand the shared Vue omnisearch store this embed's fetch config + omnisearch
  // options. This is the single config-handoff boundary between the legacy
  // Backbone app and the new store (see the custom_elements entrypoint). The
  // store fetches once; <sp-finder-omnisearch> and the specialty/provider facets read it.
  configureOmnisearchStore(ui, search) {
    const physicianDefaultIcon = window.Sparkle.apiHost
      ? window.Sparkle.apiHost + window.SparkleConfig.assetPaths.physicianDefault
      : window.SparkleConfig.assetPaths.physicianDefault;
    const locationDefaultIcon = window.Sparkle.apiHost
      ? window.Sparkle.apiHost + window.SparkleConfig.assetPaths.locationDefault
      : window.SparkleConfig.assetPaths.locationDefault;

    const config = {
      dataParams: {
        specialty_type: ui.specialtyFilterType,
        specialty_hierarchy: ui.specialtyFilterHierarchy,
        common_specialties: ui.commonSpecialtyIds,
        common_specialty_synonyms: ui.commonSpecialtySynonymIds,
        search,
      },
      savedSearches: ui.landingPage?.savedSearches,
      placeholder: ui.landingPage?.omnisearch?.placeholder,
      ariaLabel: ui.landingPage?.omnisearch?.aria_label || ui.landingPage?.omnisearch?.ariaLabel,
      requireSkill: ui.landingPage?.requireSkill,
      chatterboxEnabled: window.SparkleConfig.doctorFinder.suggestedFiltersOmnisearchSuggestion,
      textSearchEnabled: window.SparkleConfig.doctorFinder.omnisearchTextSearchEnabled,
      visitTypeFilterOwnsParam: ui.visitTypeFilterOwnsParam,
      visitTypeGroupLabel: this.I18n.t("doctor_finder.labels.autocomplete_dropdown.ehr_visit_types"),
      providerTypeGroupLabel: this.I18n.t("doctor_finder.labels.autocomplete_dropdown.provider_types"),
      recentGroupLabel: this.I18n.t("doctor_finder.labels.autocomplete_dropdown.recent"),
      suggestedFilterGroupLabel: this.I18n.t("doctor_finder.labels.autocomplete_dropdown.suggested_filters"),
      phraseSearchGroupLabel: this.I18n.t("doctor_finder.labels.autocomplete_dropdown.search_for_phrase"),
      defaultPhysicianIcon: physicianDefaultIcon,
      defaultLocationIcon: locationDefaultIcon,
    };

    // Stored on the instance so `remove` can detach it, and guarded so a late
    // firing after teardown can't reconfigure a disposed store.
    this.applyOmnisearchConfig = () => {
      if (this._torndown) {
        return;
      }
      window.Sparkle?.DoctorFinderOmnisearch?.configure?.(this.uuid, config);
    };

    if (window.spElementsInitialized) {
      this.applyOmnisearchConfig();
    } else {
      window.addEventListener("sp:elements:initialized", this.applyOmnisearchConfig, { once: true });
    }
  }

  // Presets apply static and default configuration. Static configuration is overrides values in configuration while
  // defaults fill in any missing values.
  applyUIPresetConfiguration(ui, preset) {
    const presetConfiguration = (() => {
      switch (preset) {
        case "staticList":
          return jQuery.extend(true, {}, { results: { listLayout: "default" } }, this.staticPresetConfiguration());
        case "staticSlideshow":
          return jQuery.extend(true, {}, { results: { listLayout: "slideshow" } }, this.staticPresetConfiguration());
        case "finderCompact":
          return {
            results: {
              searchHeader: false,
              hideMapView: true,
            },
          };
      }
    })();

    return jQuery.extend(true, {}, this.defaultConfiguration(), ui, presetConfiguration);
  }

  staticPresetConfiguration() {
    return {
      showFilters: false,
      results: {
        pagination: false,
        showTitle: false,
        searchHeader: false,
        hideMapView: true,
        count: false,
        summary: false,
        notices: false,
        doctorMessages: false,
      },
    };
  }

  defaultConfiguration() {
    return {
      results: {
        stickyHeaderOffset: 0, // Offset to add to sticky elements in case other fixed-position elements exist
        showOnlyNextSlot: false,
        showTitle: true,
        summary: true,
        notices: true,
        pagination: true,
        doctorMessages: true,
        maxResults: false,
        defaultSearchFilterDrawerOpen: true,
        listLayout: "default",
        defaultPerPage: window.SparkleConfig.doctorFinder.defaultPerPage,
        searchHeader: window.SparkleConfig.doctorFinder.resultControlOptions,
        scopeResultsToMapBounds: window.SparkleConfig.doctorFinder.scopeResultsToMapBounds,
      },
    };
  }

  normalizeUIOptions(ui) {
    if (ui.landingPage === undefined) {
      ui.landingPage = true;
    } // Default to showing the landing page to support legacy configurations

    // Legacy options hardcoded in client CMS templates from before https://github.com/combinaut/sparkle/commit/8f17426bfd1c120c7bf6c7767709c3374595709b used a boolean to control landing page visibility
    if (ui.landingPage === true || ui.landingPage === false) {
      ui.landingPage = {
        show: ui.landingPage,
        omnisearch: {
          placeholder: this.I18n.t("doctor_finder.placeholders.omnisearch_input_field"),
          aria_label: this.I18n.t("doctor_finder.aria_labels.omnisearch_input_field"),
        },
      };
    }

    // Legacy options hardcoded in client CMS templates would have used ui.results.showFilters instead of ui.showFilters
    if (ui.results.showFilters != null && ui.showFilters == null) {
      ui.showFilters = ui.results.showFilters;
    } // Read value from legacy option if no new option is defined

    return this.distributeUIConfiguration(ui);
  }

  // Prepares the ui configuration by distributing the common UI configuration in ui down to the landing page and results
  // configurations
  distributeUIConfiguration(ui) {
    const commonUIConfiguration = { showFilters: ui.showFilters, filters: ui.filters, filterOptions: ui.filterOptions };
    ui.landingPage = _.defaults({}, ui.landingPage, commonUIConfiguration);
    return (ui.results = _.defaults({}, ui.results, commonUIConfiguration));
  }
};
